import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import useManageTierConfigs from './useManageTierConfigs';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '600px',
      maxHeight: '90vh',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const TierConfigSetting = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [tierConfig, setTierConfig] = useState({
    id: '',
    name: '',
    tierNameTh: '',
    tierNameEn: '',
    threshold: '',
    transactionFee: '',
    extraFlowDiscount: '',
    pointEarningRate: ''
  });

  const {
    tierConfigs,
    editTierConfig,
    refreshTierConfigs
  } = useManageTierConfigs();

  const items = [
    {
      title: 'Name',
      name: 'name',
      type: 'text',
    },
    {
      title: 'Tier Name (TH)',
      name: 'tierNameTh',
      type: 'text',
    },
    {
      title: 'Tier Name (EN)',
      name: 'tierNameEn',
      type: 'text',
    },
    {
      title: 'Threshold (THB)',
      name: 'threshold',
      type: 'number',
      step: '1',
    },
    {
      title: 'Transaction Fee (THB)',
      name: 'transactionFee',
      type: 'number',
      step: '0.01',
    },
    {
      title: 'Extra Flow Discount (fraction, 0.01 = 1%)',
      name: 'extraFlowDiscount',
      type: 'number',
      step: '0.01',
    },
    {
      title: 'Point Earning Rate (THB / 1 point)',
      name: 'pointEarningRate',
      type: 'number',
      step: '0.01',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTierConfig({
      ...tierConfig,
      [name]: value
    });
  };

  const openForm = (id) => {
    const tierToEdit = tierConfigs.find(t => t.id === id);
    if (tierToEdit) {
      setTierConfig({
        id: tierToEdit.id,
        name: tierToEdit.name || '',
        tierNameTh: tierToEdit.tierNameTh || '',
        tierNameEn: tierToEdit.tierNameEn || '',
        threshold: tierToEdit.threshold || '',
        transactionFee: tierToEdit.transactionFee || '',
        extraFlowDiscount: tierToEdit.extraFlowDiscount || '',
        pointEarningRate: tierToEdit.pointEarningRate || ''
      });
      setIsFormOpen(true);
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const handleUpdateTierConfig = async () => {
    try {
      await editTierConfig(tierConfig.id, tierConfig);
      toast.success('Tier config updated successfully!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    refreshTierConfigs();
  }, []);

  return (
    <div>
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm
          items={items}
          name="Update Tier Config"
          data={tierConfig}
          mode="edit"
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleUpdate={handleUpdateTierConfig}
          stateValue={tierConfig}
        />
      </Modal>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className='flex justify-between items-center mb-4'>
            <h2 className='text-5xl'>Tier Config Setting</h2>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th scope="col" className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Name
                    </th>
                    <th scope="col" className="px-3 py-4 text-left text-sm font-semibold text-gray-900">
                      Tier Name (TH / EN)
                    </th>
                    <th scope="col" className="px-3 py-4 text-right text-sm font-semibold text-gray-900">
                      Threshold (THB)
                    </th>
                    <th scope="col" className="px-3 py-4 text-right text-sm font-semibold text-gray-900">
                      Transaction Fee (THB)
                    </th>
                    <th scope="col" className="px-3 py-4 text-right text-sm font-semibold text-gray-900">
                      Extra Flow Discount (0.01 = 1%)
                    </th>
                    <th scope="col" className="px-3 py-4 text-right text-sm font-semibold text-gray-900">
                      Point Rate (THB / point)
                    </th>
                    <th scope="col" className="relative py-4 pl-3 pr-4 sm:pr-6">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {tierConfigs.map((tier) => {
                    const { id, name, tierNameTh, tierNameEn, threshold, transactionFee, extraFlowDiscount, pointEarningRate } = tier;
                    return (
                      <tr key={id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {tierNameTh} / {tierNameEn}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          {parseFloat(threshold).toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          {transactionFee}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          {extraFlowDiscount}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                          {pointEarningRate}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <PencilIcon
                            onClick={() => openForm(id)}
                            className='cursor-pointer inline-block w-6 h-6 fill-green-600'
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TierConfigSetting;
